import { useState } from 'react';
import { Link } from 'react-router-dom';

export default function ResetPassword() {
  const token = new URLSearchParams(window.location.search).get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch('/api/auth/password-reset/confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      if (res.ok) {
        setDone(true);
      } else {
        const body = await res.json().catch(() => ({}));
        setError(typeof body.detail === 'string' ? body.detail : 'Invalid or expired reset link.');
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div className="global-watermark">
        <img src="/brand/nwg-logo-web.png" alt="NextWealth Global — Show Us the Problems. We Build the Solutions." />
      </div>
      <div style={styles.card}>
        <h1 style={styles.title}>NextWealth™ Global</h1>
        <p style={styles.subtitle}>SET A NEW PASSWORD</p>

        {done ? (
          <div style={styles.info} data-testid="reset-success">
            Password updated successfully. All previous sessions have been signed out.
            <div style={{ marginTop: 18, textAlign: 'center' }}>
              <Link to="/login" style={styles.link} data-testid="reset-success-login-link">Log in with your new password →</Link>
            </div>
          </div>
        ) : !token ? (
          <div style={styles.info} data-testid="reset-missing-token">
            This reset link is missing its token.{' '}
            <Link to="/forgot-password" style={styles.link}>Request a new link</Link>.
          </div>
        ) : (
          <form onSubmit={handleSubmit} style={styles.form}>
            <div style={styles.inputGroup}>
              <label style={styles.label}>NEW PASSWORD</label>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                minLength={6}
                style={styles.input}
                data-testid="reset-password-input"
              />
            </div>
            <div style={styles.inputGroup}>
              <label style={styles.label}>CONFIRM PASSWORD</label>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                required
                minLength={6}
                style={styles.input}
                data-testid="reset-password-confirm-input"
              />
            </div>
            {error && (
              <div style={styles.error} data-testid="reset-password-error">
                {error}{' '}
                <Link to="/forgot-password" style={styles.link}>Request a new link</Link>
              </div>
            )}
            <button type="submit" disabled={loading} style={styles.button} data-testid="reset-password-submit-button">
              {loading ? 'UPDATING...' : 'UPDATE PASSWORD'}
            </button>
          </form>
        )}

        <div style={styles.footer}>
          <Link to="/login" style={styles.link} data-testid="back-to-login-link">Back to login</Link>
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#000', padding: 20 },
  card: { width: '100%', maxWidth: 400, background: 'rgba(17,17,17,0.95)', border: '1px solid rgba(255,215,0,0.4)', borderRadius: 8, padding: '36px 32px', zIndex: 1 },
  title: { color: '#FFD700', fontSize: 26, letterSpacing: 6, textAlign: 'center', margin: 0 },
  subtitle: { color: '#999', fontSize: 11, letterSpacing: 2, textAlign: 'center', margin: '6px 0 28px' },
  form: { display: 'flex', flexDirection: 'column', gap: 16 },
  inputGroup: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#FFD700', fontSize: 11, letterSpacing: 1.5 },
  input: { padding: '12px 14px', background: 'rgba(0,0,0,0.6)', border: '1px solid rgba(255,215,0,0.3)', borderRadius: 4, color: '#fff', fontSize: 14, outline: 'none' },
  button: { padding: 14, background: '#FFD700', color: '#000', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 'bold', letterSpacing: 1.5, cursor: 'pointer' },
  error: { color: '#ff6b6b', fontSize: 13, lineHeight: 1.6 },
  info: { color: '#d4af37', fontSize: 14, lineHeight: 1.7, border: '1px solid rgba(255,215,0,0.3)', borderRadius: 4, padding: 16 },
  footer: { marginTop: 24, textAlign: 'center' },
  link: { color: '#FFD700', fontSize: 13, textDecoration: 'none', fontWeight: 'bold' },
};
